import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, CheckCircle, ArrowRight, Lightbulb, Rocket, Users,
  TrendingUp, Award, Zap, Globe, DollarSign, Target,
  Phone, Mail, Heart, Shield, Code, Database
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const InnovationPartnership = () => {
  const navigate = useNavigate();

  const opportunities = [
    {
      icon: Rocket,
      title: 'Co-Innovation Lab',
      description: 'Collaborate with our R&D team to build next-generation healthcare solutions with access to real patient data',
      color: '#1D57D5'
    },
    {
      icon: Users,
      title: 'Access to Healthcare Network',
      description: 'Connect with 2,500+ verified providers and 500K+ patients for pilot programs and product validation',
      color: '#28C08A'
    },
    {
      icon: DollarSign,
      title: 'Funding & Acceleration',
      description: 'Seed funding, mentorship, and acceleration support for promising healthcare technology startups',
      color: '#F4CE7B'
    },
    {
      icon: Database,
      title: 'Technical Infrastructure',
      description: 'Cloud hosting, API access, data storage, and computing resources for your healthcare innovation',
      color: '#9333EA'
    },
    {
      icon: Globe,
      title: 'Go-to-Market Partnership',
      description: 'Distribution channels, marketing support, and customer acquisition through GEWO\'s platform',
      color: '#F97316'
    },
    {
      icon: Shield,
      title: 'Compliance & Regulatory',
      description: 'HIPAA, GDPR, and Indian healthcare regulatory compliance guidance and certification support',
      color: '#E91E8C'
    }
  ];

  const focusAreas = [
    {
      title: 'AI & Machine Learning',
      description: 'Diagnostic AI, predictive analytics, personalized treatment recommendations, drug discovery',
      emoji: '🤖'
    },
    {
      title: 'Remote Patient Monitoring',
      description: 'Wearable devices, IoT sensors, continuous health monitoring, chronic disease management',
      emoji: '📱'
    },
    {
      title: 'Mental Health Tech',
      description: 'Digital therapeutics, mood tracking, therapy chatbots, stress management solutions',
      emoji: '🧠'
    },
    {
      title: 'Healthcare Logistics',
      description: 'Medicine delivery, ambulance optimization, cold chain management, inventory systems',
      emoji: '🚑'
    },
    {
      title: 'Patient Engagement',
      description: 'Health gamification, adherence tools, patient communities, education platforms',
      emoji: '💪'
    },
    {
      title: 'Clinical Decision Support',
      description: 'Evidence-based protocols, drug interaction checkers, diagnostic assistance, treatment pathways',
      emoji: '⚕️'
    }
  ];

  const benefits = [
    'Access to GEWO healthcare network (2,500+ providers)',
    'Cloud infrastructure & technical support',
    'Seed funding up to $100K for selected startups',
    'Mentorship from healthcare & tech leaders',
    'Pilot program with real patient data (IRB approved)',
    'Go-to-market support & customer acquisition',
    'Regulatory & compliance guidance',
    'Co-marketing opportunities',
    'Access to GEWO API platform',
    'Office space at GEWO Innovation Hub'
  ];

  const applicationProcess = [
    {
      step: '01',
      title: 'Submit Application',
      description: 'Tell us about your innovation, team, and vision for transforming healthcare',
      duration: '10 minutes'
    },
    {
      step: '02',
      title: 'Initial Review',
      description: 'Our innovation committee evaluates your submission and potential fit',
      duration: '1-2 weeks'
    },
    {
      step: '03',
      title: 'Pitch & Demo',
      description: 'Present your solution to GEWO leadership and technical team',
      duration: '1 week'
    },
    {
      step: '04',
      title: 'Partnership Kickoff',
      description: 'Onboarding, resource allocation, and roadmap planning with dedicated POC',
      duration: 'Ongoing'
    }
  ];

  const successStories = [
    {
      company: 'MediScan AI',
      focus: 'AI-powered radiology',
      result: '95% accuracy in X-ray diagnosis, now serving 50+ hospitals',
      funding: '$500K',
      gradient: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)'
    },
    {
      company: 'CareConnect IoT',
      focus: 'Remote monitoring',
      result: '10,000+ patients monitored daily, 30% hospital readmission reduction',
      funding: '$300K',
      gradient: 'linear-gradient(135deg, #28C08A 0%, #20A577 100%)'
    },
    {
      company: 'MindFlow Therapeutics',
      focus: 'Mental health',
      result: 'Digital CBT platform reaching 25K+ users, 85% treatment adherence',
      funding: '$400K',
      gradient: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)'
    }
  ];

  return (
    <div style={{ background: 'white', minHeight: '100vh' }}>
      <Navbar />
      
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #0D2C6E 0%, #1D57D5 50%, #F97316 100%)',
        paddingTop: '120px',
        paddingBottom: '100px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(244, 206, 123, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(244, 206, 123, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(244, 206, 123, 0.3)',
            borderRadius: '50px',
            padding: '10px 24px',
            marginBottom: '32px'
          }}>
            <Sparkles size={20} color="#F4CE7B" />
            <span style={{ color: '#F4CE7B', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
              FOR DIGITAL HEALTH INNOVATORS
            </span>
          </div>

          <h1 style={{
            fontSize: '64px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.1',
            marginBottom: '24px',
            letterSpacing: '-2px'
          }}>
            Innovate the Future<br />
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              of Healthcare Together
            </span>
          </h1>

          <p style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6',
            maxWidth: '650px',
            marginBottom: '40px'
          }}>
            Partner with GEWO to build, test, and scale breakthrough healthcare technologies. Access our network, funding, infrastructure, and expertise.
          </p>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/innovation-challenge')}
              style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                color: '#0D2C6E',
                border: 'none',
                borderRadius: '50px',
                padding: '18px 40px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 8px 24px rgba(244, 206, 123, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(244, 206, 123, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 206, 123, 0.4)';
              }}
            >
              Apply for Partnership
              <ArrowRight size={20} />
            </button>
            <button
              onClick={() => document.getElementById('success-stories').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50px',
                padding: '18px 40px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
              }}
            >
              View Success Stories
            </button>
          </div>

          {/* Trust Indicators */}
          <div style={{
            display: 'flex',
            gap: '48px',
            marginTop: '48px',
            flexWrap: 'wrap'
          }}>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>$2M+</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>Total Funding Deployed</div>
            </div>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>40+</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>Innovation Partners</div>
            </div>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>12</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>Products Launched</div>
            </div>
          </div>
        </div>
      </div>

      {/* What We Offer */}
      <div style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: '100px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Partnership Opportunities
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Comprehensive support to accelerate your healthcare innovation
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '32px'
          }}>
            {opportunities.map((opportunity, index) => {
              const IconComponent = opportunity.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid #E8ECF0',
                    borderRadius: '20px',
                    padding: '40px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.borderColor = opportunity.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#E8ECF0';
                  }}
                >
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '72px',
                    height: '72px',
                    background: `${opportunity.color}15`,
                    borderRadius: '16px',
                    marginBottom: '24px'
                  }}>
                    <IconComponent size={36} color={opportunity.color} />
                  </div>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '12px'
                  }}>
                    {opportunity.title}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    color: '#64748B',
                    lineHeight: '1.7'
                  }}>
                    {opportunity.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Innovation Focus Areas */}
      <div style={{ background: 'white', padding: '100px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Innovation Focus Areas
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              We're looking for breakthrough solutions in these healthcare domains
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {focusAreas.map((area, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, #F8FAFC 0%, white 100%)',
                  border: '2px solid #E8ECF0',
                  borderRadius: '20px',
                  padding: '32px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = '#1D57D5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#E8ECF0';
                }}
              >
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px'
                }}>
                  {area.emoji}
                </div>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: '#1A202C',
                  marginBottom: '12px'
                }}>
                  {area.title}
                </h3>
                <p style={{
                  fontSize: '15px',
                  color: '#64748B',
                  lineHeight: '1.7'
                }}>
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: '100px 0'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 32px' }}>
          <h2 style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#1A202C',
            marginBottom: '48px',
            textAlign: 'center',
            letterSpacing: '-1px'
          }}>
            Partnership Benefits
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {benefits.map((benefit, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  minWidth: '28px',
                  minHeight: '28px',
                  background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle size={16} color="white" />
                </div>
                <span style={{ fontSize: '16px', color: '#4A4A4A', fontWeight: '500' }}>
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Success Stories */}
      <div id="success-stories" style={{ background: 'white', padding: '100px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Innovation Success Stories
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Healthcare innovations we've helped bring to life
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '32px'
          }}>
            {successStories.map((story, index) => (
              <div
                key={index}
                style={{
                  background: 'white',
                  border: '2px solid #E8ECF0',
                  borderRadius: '20px',
                  padding: '40px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  background: story.gradient,
                  borderRadius: '16px',
                  marginBottom: '20px'
                }}>
                  <Lightbulb size={32} color="white" />
                </div>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1A202C',
                  marginBottom: '8px'
                }}>
                  {story.company}
                </h3>
                <div style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '700',
                  padding: '6px 16px',
                  borderRadius: '20px',
                  marginBottom: '16px'
                }}>
                  {story.focus}
                </div>
                <p style={{
                  fontSize: '16px',
                  color: '#4A4A4A',
                  lineHeight: '1.7',
                  marginBottom: '20px'
                }}>
                  {story.result}
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#28C08A',
                  fontSize: '18px',
                  fontWeight: '700'
                }}>
                  <DollarSign size={20} />
                  {story.funding} Funding
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Application Process */}
      <div style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: '100px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Application Process
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              From application to partnership in 4 simple steps
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '32px'
          }}>
            {applicationProcess.map((step, index) => (
              <div key={index} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                  borderRadius: '50%',
                  marginBottom: '24px',
                  boxShadow: '0 8px 24px rgba(249, 115, 22, 0.3)'
                }}>
                  <span style={{ fontSize: '28px', fontWeight: '900', color: 'white' }}>
                    {step.step}
                  </span>
                </div>
                <h4 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1A202C',
                  marginBottom: '12px'
                }}>
                  {step.title}
                </h4>
                <p style={{
                  fontSize: '15px',
                  color: '#64748B',
                  lineHeight: '1.6',
                  marginBottom: '12px'
                }}>
                  {step.description}
                </p>
                <div style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                  color: '#0D2C6E',
                  borderRadius: '20px',
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: '700'
                }}>
                  {step.duration}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{
        background: 'linear-gradient(135deg, #0D2C6E 0%, #1D57D5 50%, #F97316 100%)',
        padding: '100px 0'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '0 32px',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.2',
            marginBottom: '24px',
            letterSpacing: '-1px'
          }}>
            Ready to Innovate<br />
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Healthcare Together?
            </span>
          </h2>
          <p style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '48px',
            lineHeight: '1.6'
          }}>
            Apply for GEWO Innovation Partnership and get access to funding, infrastructure, and healthcare network.
          </p>

          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => navigate('/innovation-challenge')}
              style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                color: '#0D2C6E',
                border: 'none',
                borderRadius: '50px',
                padding: '20px 48px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 8px 24px rgba(244, 206, 123, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(244, 206, 123, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 206, 123, 0.4)';
              }}
            >
              Apply Now
              <ArrowRight size={20} />
            </button>
            <button
              onClick={() => navigate('/partners')}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50px',
                padding: '20px 48px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
              }}
            >
              View All Partnerships
            </button>
          </div>

          <div style={{
            marginTop: '48px',
            display: 'flex',
            gap: '32px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Phone size={18} color="#F4CE7B" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
                +91 99999 00000
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Mail size={18} color="#F4CE7B" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
                innovation@gewohealth.com
              </span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InnovationPartnership;
